// src/pages/sessions/components/LiveSession/whiteboard/WhiteboardManager.js
import { useState, useCallback, useRef, useEffect } from 'react';
import { Track, RoomEvent } from 'livekit-client';
import { toast } from 'sonner';
import { useWhiteboardSync } from './useWhiteboardSync';
import { useWhiteboardBroadcastChannel } from './useWhiteboardBroadcastChannel';

// LiveKit data channel max message size (64KB with some buffer for overhead)
const MAX_MESSAGE_SIZE = 60000;

/**
 * Reviver function for Fabric.js v6 loadFromJSON.
 * v6 signature: loadFromJSON(json, reviver?, options?) → Promise
 * v6 reviver: (serializedObj, instance) => void
 */
function fabricReviver(serializedObj, instance) {
  if (serializedObj.objectId) instance.objectId = serializedObj.objectId;
  if (serializedObj.authorIdentity) instance.authorIdentity = serializedObj.authorIdentity;
}

/**
 * Post-load fixup: forcefully restore objectId/authorIdentity from source JSON.
 * loadFromJSON preserves object order, so we match by index.
 * This is the final safety net — works regardless of whether the reviver fired,
 * customProperties is set, or Fabric dropped properties during fromObject.
 */
function restoreCustomProps(canvas, sourceJSON) {
  if (!canvas || !sourceJSON) return;
  const objects = canvas.getObjects();
  const sourceObjects = (typeof sourceJSON === 'string' ? JSON.parse(sourceJSON) : sourceJSON).objects || [];
  for (let i = 0; i < objects.length && i < sourceObjects.length; i++) {
    if (sourceObjects[i].objectId && !objects[i].objectId) {
      objects[i].objectId = sourceObjects[i].objectId;
    }
    if (sourceObjects[i].authorIdentity && !objects[i].authorIdentity) {
      objects[i].authorIdentity = sourceObjects[i].authorIdentity;
    }
  }
}

// Default settings per tool
const DEFAULT_TOOL_SETTINGS = {
  select: { color: '#000000', size: 4 },
  marker: { color: '#000000', size: 4 },
  brush: { color: '#000000', size: 4 },
  highlighter: { color: '#FFFF00', size: 8 },
  eraser: { color: '#000000', size: 4 },
  line: { color: '#000000', size: 4 },
  arrow: { color: '#000000', size: 4 },
  rectangle: { color: '#000000', size: 4 },
  circle: { color: '#000000', size: 4 },
  filledRectangle: { color: '#000000', size: 4 },
  filledCircle: { color: '#000000', size: 4 },
  text: { color: '#000000', size: 4 },
};

/**
 * Simplify a Fabric.js path by reducing the number of points
 * Uses Ramer-Douglas-Peucker-like decimation
 */
function simplifyPath(pathData, tolerance = 1) {
  if (!pathData || !Array.isArray(pathData)) return pathData;
  
  // Extract points from path commands
  const points = [];
  for (const cmd of pathData) {
    if (Array.isArray(cmd) && cmd.length >= 3) {
      // M, L commands have x,y at indices 1,2
      // Q commands have control points and end point
      // C commands have two control points and end point
      const cmdType = cmd[0];
      if (cmdType === 'M' || cmdType === 'L') {
        points.push({ x: cmd[1], y: cmd[2], cmd: cmd });
      } else if (cmdType === 'Q') {
        points.push({ x: cmd[3], y: cmd[4], cmd: cmd });
      } else if (cmdType === 'C') {
        points.push({ x: cmd[5], y: cmd[6], cmd: cmd });
      }
    }
  }
  
  if (points.length <= 2) return pathData;
  
  // Simple decimation - keep every Nth point based on tolerance
  const keepEvery = Math.max(1, Math.floor(tolerance));
  const simplified = [pathData[0]]; // Always keep first command (M)
  
  for (let i = 1; i < pathData.length - 1; i++) {
    if (i % keepEvery === 0) {
      simplified.push(pathData[i]);
    }
  }
  
  // Always keep last command
  if (pathData.length > 1) {
    simplified.push(pathData[pathData.length - 1]);
  }
  
  return simplified;
}

/**
 * Prepare fabricJSON for broadcasting - simplify if too large
 */
function prepareForBroadcast(fabricJSON) {
  let json = { ...fabricJSON };
  let jsonStr = JSON.stringify(json);
  
  // If small enough, return as-is
  if (jsonStr.length < MAX_MESSAGE_SIZE) {
    return json;
  }
  
  // If it's a path, try to simplify it (reduces point count for smoother sync)
  if (json.type === 'path' && json.path) {
    let tolerance = 2;
    while (jsonStr.length >= MAX_MESSAGE_SIZE && tolerance <= 20) {
      json = { ...fabricJSON, path: simplifyPath(fabricJSON.path, tolerance) };
      jsonStr = JSON.stringify(json);
      tolerance += 2;
    }
  }
  
  // Even if still large after simplification, return it anyway —
  // broadcastMessage will chunk it automatically via the data channel.
  return json;
}

/**
 * WhiteboardManager - Main orchestrator hook for whiteboard feature
 * Manages: canvas state, sync, permissions, egress track publishing
 */
export function useWhiteboardManager({
  roomRef,
  roomConnected,
  isTeacherUser,
  localParticipantIdentity,
  currentVirtualRoom,
  teacherBroadcastRooms,
  remoteTeacherStates = {},
  sessionId,
}) {
  // Whiteboard active state
  const [isWhiteboardActive, setIsWhiteboardActive] = useState(false);
  
  // Tool state - per-tool settings
  const [activeTool, setActiveTool] = useState('marker');
  const [toolSettings, setToolSettings] = useState(DEFAULT_TOOL_SETTINGS);
  const [hasSelection, setHasSelection] = useState(false);
  
  // Permission state
  const [studentDrawingEnabled, setStudentDrawingEnabled] = useState(false);
  const [allowedStudents, setAllowedStudents] = useState(null); // null = all, array = specific
  
  // Remote whiteboard state (for students)
  const [remoteWhiteboardActive, setRemoteWhiteboardActive] = useState(false);
  const [remotePermissions, setRemotePermissions] = useState({ canDraw: false, allowedIdentities: null });
  
  // Refs
  const fabricCanvasRef = useRef(null);
  const objectsByIdRef = useRef(new Map());
  const isApplyingRemoteRef = useRef(0); // Counter instead of boolean — async ops increment/decrement
  const removedIdsRef = useRef(new Set()); // Tombstones: track removed object IDs to reject late-arriving adds
  const egressTrackRef = useRef(null);
  const egressStreamRef = useRef(null);
  const mountedRef = useRef(true);
  const hasSyncedRef = useRef(false);
  const broadcastMessageRef = useRef(null);
  const pendingMessagesRef = useRef([]); // Queue for messages that arrive before canvas is ready
  const canvasReadyRef = useRef(false); // Track if canvas is ready
  const liveStrokesRef = useRef(new Map()); // Track in-progress strokes from remote participants
  const whiteboardOwnerRef = useRef(null); // Track who opened the whiteboard (teacher identity)
  const wasInBroadcastRef = useRef(false); // Track if student was in broadcast rooms
  const reconcileTimerRef = useRef(null); // Periodic reconciliation timer
  
  // Detached whiteboard state
  const [isDetached, setIsDetached] = useState(false);
  const detachedWindowRef = useRef(null);
  const detachedCanvasSnapshotRef = useRef(null); // Cached canvas state for detach/reattach
  const isDetachedRef = useRef(false);
  const sendToDetachedRef = useRef(null);
  const unpublishEgressTrackRef = useRef(null); // Ref for egress cleanup from BroadcastChannel handler
  const attachWhiteboardRef = useRef(null); // Ref for reattach from BroadcastChannel handler
  
  // Refs for sync closure access
  const currentVirtualRoomRef = useRef(currentVirtualRoom);
  const teacherBroadcastRoomsRef = useRef(new Set(teacherBroadcastRooms || []));
  const remotePermissionsRef = useRef(remotePermissions);
  const studentDrawingEnabledRef = useRef(studentDrawingEnabled);
  const allowedStudentsRef = useRef(allowedStudents);
  const isWhiteboardActiveRef = useRef(isWhiteboardActive);
  const localParticipantIdentityRef = useRef(localParticipantIdentity);
  
  // Keep refs updated
  useEffect(() => {
    currentVirtualRoomRef.current = currentVirtualRoom;
  }, [currentVirtualRoom]);
  
  useEffect(() => {
    teacherBroadcastRoomsRef.current = new Set(teacherBroadcastRooms || []);
    
    // For students in breakout rooms: handle broadcast changes using remoteTeacherStates
    // (teacherBroadcastRooms is the local user's state — empty for students)
    if (!isTeacherUser && remoteWhiteboardActive && currentVirtualRoom) {
      const roomId = currentVirtualRoom.id || currentVirtualRoom;
      
      // Check if any teacher is broadcasting to this student's room
      let isNowInBroadcast = false;
      for (const [, teacherState] of Object.entries(remoteTeacherStates)) {
        const broadcasting = teacherState?.broadcastingTo || [];
        if (broadcasting.some(r => Number(r) === Number(roomId))) {
          isNowInBroadcast = true;
          break;
        }
      }
      
      // If student just got added to broadcast rooms, reset sync flag to request sync
      if (isNowInBroadcast && !wasInBroadcastRef.current) {
        hasSyncedRef.current = false;
      }
      
      // If student was in broadcast but no longer is, clear the canvas
      if (!isNowInBroadcast && wasInBroadcastRef.current) {
        const canvas = fabricCanvasRef.current;
        if (canvas) {
          canvas.clear();
          canvas.backgroundColor = '#ffffff';
          objectsByIdRef.current.clear();
          liveStrokesRef.current.clear();
          canvas.renderAll();
        }
        hasSyncedRef.current = false;
      }
      
      wasInBroadcastRef.current = isNowInBroadcast;
    }
  }, [remoteTeacherStates, isTeacherUser, remoteWhiteboardActive, currentVirtualRoom]);
  
  useEffect(() => {
    remotePermissionsRef.current = remotePermissions;
  }, [remotePermissions]);
  
  useEffect(() => {
    studentDrawingEnabledRef.current = studentDrawingEnabled;
    allowedStudentsRef.current = allowedStudents;
  }, [studentDrawingEnabled, allowedStudents]);

  useEffect(() => {
    isWhiteboardActiveRef.current = isWhiteboardActive;
  }, [isWhiteboardActive]);
  
  useEffect(() => {
    localParticipantIdentityRef.current = localParticipantIdentity;
  }, [localParticipantIdentity]);
  
  useEffect(() => {
    isDetachedRef.current = isDetached;
  }, [isDetached]);

  // Get current tool's color and size
  const getCurrentToolSettings = useCallback(() => {
    return toolSettings[activeTool] || DEFAULT_TOOL_SETTINGS.marker || { color: '#000000', size: 4 };
  }, [toolSettings, activeTool]);

  // Update tool settings
  const updateToolSettings = useCallback((tool, updates) => {
    setToolSettings(prev => ({
      ...prev,
      [tool]: { ...prev[tool], ...updates }
    }));
  }, []);

  // Generate unique object ID
  const generateObjectId = useCallback(() => {
    return `obj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }, []);

  // Check if local user can draw
  const canLocalUserDraw = useCallback((permissions = remotePermissionsRef.current) => {
    if (isTeacherUser) return true;
    if (!permissions?.canDraw) return false;
    if (permissions.allowedIdentities === null) return true;
    return permissions.allowedIdentities?.includes(localParticipantIdentity);
  }, [isTeacherUser, localParticipantIdentity]);

  // Reapply canvas config after loadFromJSON (Fabric.js can reset these)
  const reapplyCanvasConfig = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    
    const canDraw = canLocalUserDraw();
    
    if (!canDraw) {
      canvas.isDrawingMode = false;
      canvas.selection = false;
      canvas.skipTargetFind = true;
      canvas.hoverCursor = 'default';
    }
    
    canvas.forEachObject(obj => {
      obj.selectable = canDraw;
      obj.evented = canDraw;
    });
  }, [canLocalUserDraw]);

  // Process a single sync message (extracted for reuse with queue)
  const processSyncMessage = useCallback((msg) => {
    switch (msg.type) {
      case 'whiteboard_state': {
        console.log('[Whiteboard] Received whiteboard_state:', {
          active: msg.active,
          author: msg.authorIdentity,
          currentRoom: currentVirtualRoomRef.current?.id || currentVirtualRoomRef.current
        });
        setRemoteWhiteboardActive(msg.active);
        if (msg.active) {
          // Track who opened the whiteboard
          whiteboardOwnerRef.current = msg.authorIdentity || null;
        } else {
          hasSyncedRef.current = false;
          whiteboardOwnerRef.current = null;
        }
        break;
      }
      
      case 'whiteboard_state_request': {
        // Teacher responds to state requests from late-joining students
        if (!isTeacherUser) return;
        
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current({
            type: 'whiteboard_state',
            boardId: 'main',
            active: isWhiteboardActiveRef.current,
            authorIdentity: localParticipantIdentityRef.current,
          });
          
          // Also send permissions if whiteboard is active
          if (isWhiteboardActiveRef.current) {
            broadcastMessageRef.current({
              type: 'whiteboard_permission',
              boardId: 'main',
              canDraw: studentDrawingEnabledRef.current,
              allowedIdentities: allowedStudentsRef.current,
            });
          }
        }
        break;
      }
      
      case 'whiteboard_permission': {
        const newPermissions = {
          canDraw: msg.canDraw,
          allowedIdentities: msg.allowedIdentities,
        };
        setRemotePermissions(newPermissions);
        remotePermissionsRef.current = newPermissions;
        
        // Reapply canvas config with new permissions
        if (fabricCanvasRef.current) {
          reapplyCanvasConfig();
        }
        break;
      }
      
      case 'whiteboard_sync': {
        const currentCanvas = fabricCanvasRef.current;
        if (!currentCanvas) {
          return false; // Signal to queue this message
        }
        
        isApplyingRemoteRef.current++;
        const syncCanvasJSON = msg.canvasJSON;
        
        // Guard against double-callback (Fabric callback + Promise .then)
        let syncCompleted = false;
        const onSyncComplete = () => {
          if (syncCompleted) return;
          syncCompleted = true;
          
          // Force-restore custom properties from source JSON (safety net)
          restoreCustomProps(currentCanvas, syncCanvasJSON);
          
          // Rebuild objectsByIdRef and clear tombstones (full sync = authoritative state)
          objectsByIdRef.current.clear();
          removedIdsRef.current.clear();
          currentCanvas.getObjects().forEach(obj => {
            if (obj.objectId) {
              objectsByIdRef.current.set(obj.objectId, obj);
            }
          });
          
          // Reapply canvas config (Fabric.js loadFromJSON can reset settings)
          const newPermissions = msg.permissions || { canDraw: false, allowedIdentities: null };
          setRemotePermissions(newPermissions);
          remotePermissionsRef.current = newPermissions;
          reapplyCanvasConfig();
          
          currentCanvas.renderAll();
          isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        };
        
        // Fabric.js v6: loadFromJSON(json, reviver?) → Promise
        try {
          currentCanvas.loadFromJSON(syncCanvasJSON, fabricReviver)
            .then(onSyncComplete)
            .catch(err => {
              console.error('[Whiteboard] loadFromJSON error:', err);
              isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
            });
        } catch (err) {
          console.error('[Whiteboard] loadFromJSON error:', err);
          isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        }
        break;
      }
      
      case 'whiteboard_sync_request': {
        // Teacher responds to sync requests
        if (!isTeacherUser) return;
        
        // When detached, request canvas state from detached tab
        if (isDetachedRef.current && sendToDetachedRef.current) {
          sendToDetachedRef.current('request_canvas_for_sync', {});
          return;
        }
        
        const currentCanvas = fabricCanvasRef.current;
        if (!isTeacherUser || !currentCanvas) return;
        
        const response = {
          type: 'whiteboard_sync',
          boardId: 'main',
          canvasJSON: currentCanvas.toJSON(['objectId', 'authorIdentity']),
          permissions: {
            canDraw: studentDrawingEnabledRef.current,
            allowedIdentities: allowedStudentsRef.current,
          },
        };
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current(response);
        }
        break;
      }
      
      case 'wb_object_added': {
        // When detached, forward student drawings to detached tab instead of local canvas
        if (isDetachedRef.current && sendToDetachedRef.current) {
          sendToDetachedRef.current('remote_object_added', {
            objectId: msg.objectId,
            fabricJSON: msg.fabricJSON,
          });
          break;
        }
        const currentCanvas = fabricCanvasRef.current;
        if (!currentCanvas) {
          return false; // Signal to queue this message
        }
        // Idempotency guard
        if (objectsByIdRef.current.has(msg.objectId)) {
          return;
        }
        // Tombstone guard: reject adds for objects that were already removed
        // (handles out-of-order add/remove messages)
        if (removedIdsRef.current.has(msg.objectId)) {
          return;
        }
        
        isApplyingRemoteRef.current++;
        
        const fabric = currentCanvas._fabricModule;
        if (!fabric) {
          isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
          return;
        }
        
        const json = msg.fabricJSON;
        
        const addObjectToCanvas = (obj) => {
          if (!obj) {
            isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
            return;
          }
          
          obj.objectId = msg.objectId;
          obj.authorIdentity = msg.authorIdentity;
          
          // Apply view-only settings
          const canDraw = canLocalUserDraw();
          if (!canDraw) {
            obj.selectable = false;
            obj.evented = false;
          }
          
          currentCanvas.add(obj);
          objectsByIdRef.current.set(msg.objectId, obj);
          
          currentCanvas.renderAll();
          isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        };
        
        // Direct object creation based on type (most reliable across Fabric versions)
        try {
          let obj = null;
          
          if (json.type === 'path' && fabric.Path) {
            obj = new fabric.Path(json.path, {
              ...json,
              path: undefined,
            });
            addObjectToCanvas(obj);
          } else if (json.type === 'rect' && fabric.Rect) {
            obj = new fabric.Rect(json);
            addObjectToCanvas(obj);
          } else if (json.type === 'circle' && fabric.Circle) {
            obj = new fabric.Circle(json);
            addObjectToCanvas(obj);
          } else if (json.type === 'line' && fabric.Line) {
            obj = new fabric.Line([json.x1, json.y1, json.x2, json.y2], json);
            addObjectToCanvas(obj);
          } else if (json.type === 'textbox' && fabric.Textbox) {
            obj = new fabric.Textbox(json.text || '', json);
            addObjectToCanvas(obj);
          } else if (json.type === 'text' && fabric.Text) {
            obj = new fabric.Text(json.text || '', json);
            addObjectToCanvas(obj);
          } else if (json.type === 'i-text' && fabric.IText) {
            obj = new fabric.IText(json.text || '', json);
            addObjectToCanvas(obj);
          } else if (json.type === 'group' && fabric.Group) {
            // Groups (used for arrow shapes, etc.)
            const enlivenFn = fabric.util?.enlivenObjects;
            if (enlivenFn && json.objects) {
              const result = enlivenFn(json.objects);
              if (result && typeof result.then === 'function') {
                result.then(children => {
                  obj = new fabric.Group(children, { ...json, objects: undefined });
                  addObjectToCanvas(obj);
                }).catch(err => {
                  console.error('[Whiteboard] group enlivenObjects error:', err);
                  isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
                });
              } else {
                enlivenFn(json.objects, (children) => {
                  obj = new fabric.Group(children, { ...json, objects: undefined });
                  addObjectToCanvas(obj);
                });
              }
            } else {
              isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
            }
          } else if (json.type === 'image' && fabric.Image) {
            // Image objects
            const loadImage = fabric.Image.fromURL || fabric.Image.fromObject;
            if (json.src && fabric.Image.fromURL) {
              const result = fabric.Image.fromURL(json.src, (img) => {
                if (img) {
                  img.set(json);
                  addObjectToCanvas(img);
                } else {
                  isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
                }
              });
              // Handle promise-based Fabric v6
              if (result && typeof result.then === 'function') {
                result.then(img => {
                  if (img) { img.set(json); addObjectToCanvas(img); }
                  else { isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1); }
                }).catch(() => {
                  isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
                });
              }
            } else {
              isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
            }
          } else if (fabric.util && fabric.util.enlivenObjects) {
            // Fallback to enlivenObjects for other types
            const result = fabric.util.enlivenObjects([json]);
            
            if (result && typeof result.then === 'function') {
              result.then(objects => addObjectToCanvas(objects?.[0])).catch(err => {
                console.error('[Whiteboard] enlivenObjects error:', err);
                isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
              });
            } else {
              fabric.util.enlivenObjects([json], (objects) => addObjectToCanvas(objects?.[0]));
            }
          } else {
            console.error('[Whiteboard] Cannot create object of type:', json.type);
            isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
          }
        } catch (err) {
          console.error('[Whiteboard] wb_object_added error:', err);
          isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        }
        break;
      }
      
      case 'wb_object_modified': {
        if (isDetachedRef.current && sendToDetachedRef.current) {
          sendToDetachedRef.current('remote_object_modified', {
            objectId: msg.objectId,
            transform: msg.transform,
          });
          break;
        }
        const currentCanvas = fabricCanvasRef.current;
        if (!currentCanvas) return false;
        const obj = objectsByIdRef.current.get(msg.objectId);
        if (!obj) return;
        
        isApplyingRemoteRef.current++;
        obj.set(msg.transform);
        // Recalculate text dimensions if text content/style changed
        if (obj.initDimensions) obj.initDimensions();
        obj.setCoords();
        currentCanvas.renderAll();
        isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        break;
      }
      
      case 'wb_object_removed': {
        if (isDetachedRef.current && sendToDetachedRef.current) {
          sendToDetachedRef.current('remote_object_removed', {
            objectId: msg.objectId,
          });
          break;
        }
        const currentCanvas = fabricCanvasRef.current;
        if (!currentCanvas) return false;
        
        // Always record tombstone — even if object doesn't exist yet (out-of-order messages)
        removedIdsRef.current.add(msg.objectId);
        
        const obj = objectsByIdRef.current.get(msg.objectId);
        if (!obj) return; // Object may not exist locally yet, tombstone handles it
        
        isApplyingRemoteRef.current++;
        currentCanvas.remove(obj);
        objectsByIdRef.current.delete(msg.objectId);
        currentCanvas.renderAll();
        isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        break;
      }
      
      case 'wb_clear': {
        if (isDetachedRef.current && sendToDetachedRef.current) {
          sendToDetachedRef.current('canvas_cleared', {});
          break;
        }
        const currentCanvas = fabricCanvasRef.current;
        if (!currentCanvas) return false;
        
        isApplyingRemoteRef.current++;
        currentCanvas.clear();
        currentCanvas.backgroundColor = '#ffffff';
        objectsByIdRef.current.clear();
        removedIdsRef.current.clear(); // Clear tombstones — clean slate
        liveStrokesRef.current.clear(); // Also clear live strokes
        currentCanvas.renderAll();
        isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        break;
      }
      
      // ===== LIGHTWEIGHT RECONCILIATION (no canvas clear/flash) =====
      case 'wb_reconcile': {
        if (isDetachedRef.current) break; // Skip reconciliation when detached
        const currentCanvas = fabricCanvasRef.current;
        if (!currentCanvas) return false;
        
        const manifest = msg.manifest || {};
        const remoteIds = new Set(Object.keys(manifest));
        const localIds = new Set(objectsByIdRef.current.keys());
        let changed = false;
        
        isApplyingRemoteRef.current++;
        
        // 1. Remove local objects that teacher no longer has (ghost cleanup)
        for (const localId of localIds) {
          if (!remoteIds.has(localId)) {
            const obj = objectsByIdRef.current.get(localId);
            if (obj) {
              currentCanvas.remove(obj);
              objectsByIdRef.current.delete(localId);
              changed = true;
            }
          }
        }
        
        // 2. Add objects that teacher has but we don't
        const fabric = currentCanvas._fabricModule;
        if (fabric) {
          for (const remoteId of remoteIds) {
            if (!localIds.has(remoteId)) {
              const json = manifest[remoteId];
              if (!json) continue;
              
              // Reuse the same object creation as wb_object_added
              try {
                let obj = null;
                const addObj = (o) => {
                  if (!o) return;
                  o.objectId = remoteId;
                  o.authorIdentity = json.authorIdentity;
                  const canDraw = canLocalUserDraw();
                  if (!canDraw) { o.selectable = false; o.evented = false; }
                  currentCanvas.add(o);
                  objectsByIdRef.current.set(remoteId, o);
                  changed = true;
                };
                
                if (json.type === 'path' && fabric.Path) {
                  obj = new fabric.Path(json.path, { ...json, path: undefined });
                  addObj(obj);
                } else if (json.type === 'rect' && fabric.Rect) {
                  addObj(new fabric.Rect(json));
                } else if (json.type === 'circle' && fabric.Circle) {
                  addObj(new fabric.Circle(json));
                } else if (json.type === 'line' && fabric.Line) {
                  addObj(new fabric.Line([json.x1, json.y1, json.x2, json.y2], json));
                } else if (json.type === 'textbox' && fabric.Textbox) {
                  addObj(new fabric.Textbox(json.text || '', json));
                } else if (json.type === 'text' && fabric.Text) {
                  addObj(new fabric.Text(json.text || '', json));
                } else if (json.type === 'i-text' && fabric.IText) {
                  addObj(new fabric.IText(json.text || '', json));
                } else if (fabric.util?.enlivenObjects) {
                  const result = fabric.util.enlivenObjects([json]);
                  if (result && typeof result.then === 'function') {
                    result.then(objects => { addObj(objects?.[0]); if (changed) currentCanvas.renderAll(); });
                  } else {
                    fabric.util.enlivenObjects([json], (objects) => { addObj(objects?.[0]); if (changed) currentCanvas.renderAll(); });
                  }
                }
              } catch (err) {
                // Skip failed objects silently — next reconcile will retry
              }
            }
          }
        }
        
        // 3. Update existing objects with changed properties
        for (const remoteId of remoteIds) {
          if (localIds.has(remoteId)) {
            const localObj = objectsByIdRef.current.get(remoteId);
            const remoteJson = manifest[remoteId];
            if (!localObj || !remoteJson) continue;
            
            // Quick check: compare key properties that affect appearance
            const propsToCheck = ['left', 'top', 'scaleX', 'scaleY', 'angle', 'text', 'fill', 'stroke', 'backgroundColor', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'width'];
            let needsUpdate = false;
            for (const prop of propsToCheck) {
              if (remoteJson[prop] !== undefined && localObj[prop] !== remoteJson[prop]) {
                needsUpdate = true;
                break;
              }
            }
            
            if (needsUpdate) {
              // Only update the safe visual properties, don't reconstruct the object
              const safeProps = {};
              propsToCheck.forEach(p => {
                if (remoteJson[p] !== undefined) safeProps[p] = remoteJson[p];
              });
              localObj.set(safeProps);
              if (localObj.initDimensions) localObj.initDimensions();
              localObj.setCoords();
              changed = true;
            }
          }
        }
        
        if (changed) {
          currentCanvas.renderAll();
        }
        
        // Clear tombstones — teacher state is authoritative
        removedIdsRef.current.clear();
        isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
        break;
      }
      
      // ===== LIVE STROKE STREAMING =====
      case 'wb_stroke_start': {
        if (isDetachedRef.current && sendToDetachedRef.current) {
          sendToDetachedRef.current('remote_stroke_start', {
            strokeId: msg.strokeId,
            point: msg.point,
            color: msg.color,
            width: msg.width,
            opacity: msg.opacity,
          });
          break;
        }
        const currentCanvas = fabricCanvasRef.current;
        if (!currentCanvas) return false;
        
        const fabric = currentCanvas._fabricModule;
        if (!fabric) return false;
        
        const { strokeId, point, color, width, opacity } = msg;
        
        // Use Polyline for live stroke - easier to add points dynamically
        const tempLine = new fabric.Polyline([{ x: point.x, y: point.y }], {
          stroke: color,
          strokeWidth: width,
          fill: null,
          opacity: opacity || 1,
          strokeLineCap: 'round',
          strokeLineJoin: 'round',
          selectable: false,
          evented: false,
          objectCaching: false,
        });
        
        tempLine._isLiveStroke = true;
        tempLine._strokeId = strokeId;
        
        currentCanvas.add(tempLine);
        liveStrokesRef.current.set(strokeId, tempLine);
        currentCanvas.renderAll();
        break;
      }
      
      case 'wb_stroke_point': {
        if (isDetachedRef.current && sendToDetachedRef.current) {
          sendToDetachedRef.current('remote_stroke_point', {
            strokeId: msg.strokeId,
            point: msg.point,
          });
          break;
        }
        const currentCanvas = fabricCanvasRef.current;
        if (!currentCanvas) return false;
        
        const { strokeId, point } = msg;
        const tempLine = liveStrokesRef.current.get(strokeId);
        
        if (!tempLine || !tempLine.points) return;
        
        // Add new point directly to the polyline's points array
        tempLine.points.push({ x: point.x, y: point.y });
        
        // Force recalculation and redraw
        if (typeof tempLine._setPositionDimensions === 'function') {
          tempLine._setPositionDimensions({});
        }
        tempLine.setCoords();
        tempLine.dirty = true;
        currentCanvas.requestRenderAll();
        break;
      }
      
      case 'wb_stroke_end': {
        if (isDetachedRef.current && sendToDetachedRef.current) {
          sendToDetachedRef.current('remote_stroke_end', {
            strokeId: msg.strokeId,
          });
          break;
        }
        const { strokeId } = msg;
        const tempLine = liveStrokesRef.current.get(strokeId);
        
        if (tempLine) {
          const currentCanvas = fabricCanvasRef.current;
          if (currentCanvas) {
            // Remove the temporary live stroke - the final path will come via wb_object_added
            currentCanvas.remove(tempLine);
            currentCanvas.renderAll();
          }
          liveStrokesRef.current.delete(strokeId);
        }
        break;
      }
    }
    
    return true; // Message was processed
  }, [isTeacherUser, canLocalUserDraw, reapplyCanvasConfig]);

  // Handle incoming sync messages - queue if canvas not ready
  const handleSyncMessage = useCallback((msg) => {
    console.log('[Whiteboard] handleSyncMessage:', msg.type, {
      hasCanvas: !!fabricCanvasRef.current,
      isDetached: isDetachedRef.current,
      shouldShow: prevShouldShowRef.current,
    });
    
    // Messages that need canvas
    const needsCanvas = ['whiteboard_sync', 'wb_object_added', 'wb_object_modified', 'wb_object_removed', 'wb_clear', 'wb_stroke_start', 'wb_stroke_point', 'wb_stroke_end', 'wb_reconcile'];
    
    // If canvas not ready and message needs it, check if we should forward to detached tab.
    // When detached, the main tab has no canvas (it's in the detached window), but
    // processSyncMessage knows how to forward messages via BroadcastChannel — let it through.
    if (needsCanvas.includes(msg.type) && !fabricCanvasRef.current && !isDetachedRef.current) {
      pendingMessagesRef.current.push(msg);
      return;
    }
    
    processSyncMessage(msg);
  }, [processSyncMessage]);

  // Sync hook - needs broadcastMessage before handleSyncMessage can use it
  const { broadcastMessage } = useWhiteboardSync({
    roomRef,
    roomConnected,
    localParticipantIdentity,
    isTeacherUser,
    currentVirtualRoomRef,
    teacherBroadcastRoomsRef,
    onMessage: handleSyncMessage,
  });
  
  // Store broadcastMessage in ref for use in handleSyncMessage
  useEffect(() => {
    broadcastMessageRef.current = broadcastMessage;
  }, [broadcastMessage]);

  // === BroadcastChannel: handle messages from detached whiteboard tab ===
  const handleDetachedMessage = useCallback((type, payload) => {
    switch (type) {
      case 'request_sync': {
        // Detached tab requesting initial canvas state
        // If whiteboard isn't active on main tab (e.g. tab was refreshed), tell detached to close
        if (!isWhiteboardActiveRef.current) {
          if (sendToDetachedRef.current) {
            sendToDetachedRef.current('whiteboard_closed', {});
          }
          break;
        }
        const canvas = fabricCanvasRef.current;
        const canvasJSON = canvas
          ? canvas.toJSON(['objectId', 'authorIdentity'])
          : (detachedCanvasSnapshotRef.current || { version: '5.3.0', objects: [] });
        if (sendToDetachedRef.current) {
          sendToDetachedRef.current('canvas_sync', {
            canvasJSON,
            studentDrawingEnabled: studentDrawingEnabledRef.current,
          });
        }
        break;
      }

      // === Drawing events from detached tab → broadcast via LiveKit ===
      case 'drawing_path_created':
      case 'drawing_shape_created':
      case 'drawing_text_created':
      case 'drawing_image_added': {
        if (broadcastMessageRef.current) {
          // Simplify path if possible (chunking handles large messages)
          const fabricJSON = prepareForBroadcast(payload.fabricJSON);
          broadcastMessageRef.current({
            type: 'wb_object_added',
            boardId: 'main',
            objectId: payload.objectId,
            fabricJSON,
            authorIdentity: localParticipantIdentityRef.current,
          });
        }
        break;
      }

      case 'drawing_stroke_start': {
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current({
            type: 'wb_stroke_start',
            boardId: 'main',
            strokeId: payload.strokeId,
            authorIdentity: localParticipantIdentityRef.current,
            point: payload.point,
            color: payload.color,
            width: payload.width,
            opacity: payload.opacity,
          });
        }
        break;
      }

      case 'drawing_stroke_point': {
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current({
            type: 'wb_stroke_point',
            boardId: 'main',
            strokeId: payload.strokeId,
            authorIdentity: localParticipantIdentityRef.current,
            point: payload.point,
          });
        }
        break;
      }

      case 'drawing_stroke_end': {
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current({
            type: 'wb_stroke_end',
            boardId: 'main',
            strokeId: payload.strokeId,
            authorIdentity: localParticipantIdentityRef.current,
          });
        }
        break;
      }

      case 'drawing_object_modified': {
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current({
            type: 'wb_object_modified',
            boardId: 'main',
            objectId: payload.objectId,
            authorIdentity: localParticipantIdentityRef.current,
            transform: payload.transform,
          });
        }
        break;
      }

      case 'drawing_object_erased': {
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current({
            type: 'wb_object_removed',
            boardId: 'main',
            objectId: payload.objectId,
            authorIdentity: localParticipantIdentityRef.current,
          });
        }
        break;
      }

      case 'drawing_clear': {
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current({
            type: 'wb_clear',
            boardId: 'main',
            authorIdentity: localParticipantIdentityRef.current,
          });
        }
        // Also clear local refs
        objectsByIdRef.current.clear();
        removedIdsRef.current.clear();
        break;
      }

      case 'drawing_close_whiteboard': {
        // Close whiteboard from detached tab — inline the close logic
        setIsWhiteboardActive(false);
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current({
            type: 'whiteboard_state',
            boardId: 'main',
            active: false,
            authorIdentity: localParticipantIdentityRef.current,
          });
        }
        // Unpublish egress track
        if (unpublishEgressTrackRef.current) {
          unpublishEgressTrackRef.current();
        }
        // Clean up close-monitor interval
        if (checkClosedIntervalRef.current) {
          clearInterval(checkClosedIntervalRef.current);
          checkClosedIntervalRef.current = null;
        }
        setIsDetached(false);
        detachedWindowRef.current = null;
        toast.info('Whiteboard closed');
        break;
      }

      case 'drawing_reattach_request': {
        // Detached tab wants to return to main tab
        if (attachWhiteboardRef.current) {
          attachWhiteboardRef.current();
        }
        break;
      }

      case 'drawing_toggle_student_drawing': {
        // Toggle student drawing from detached tab — inline the toggle logic
        setStudentDrawingEnabled(payload.enabled);
        if (!payload.enabled) {
          setAllowedStudents(null);
        }
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current({
            type: 'whiteboard_permission',
            boardId: 'main',
            canDraw: payload.enabled,
            allowedIdentities: payload.enabled ? allowedStudentsRef.current : null,
          });
        }
        break;
      }

      case 'sync_response': {
        // Detached tab responded with canvas state (for student sync request)
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current({
            type: 'whiteboard_sync',
            boardId: 'main',
            canvasJSON: payload.canvasJSON,
            permissions: {
              canDraw: studentDrawingEnabledRef.current,
              allowedIdentities: allowedStudentsRef.current,
            },
          });
        }
        break;
      }

      case 'canvas_snapshot': {
        // Detached tab sending its canvas state (for reattach or beforeunload)
        detachedCanvasSnapshotRef.current = payload.canvasJSON;
        break;
      }

      case 'detached_closing': {
        // Detached tab closed
        if (checkClosedIntervalRef.current) {
          clearInterval(checkClosedIntervalRef.current);
          checkClosedIntervalRef.current = null;
        }
        setIsDetached(false);
        detachedWindowRef.current = null;
        break;
      }

      default:
        break;
    }
  }, []);

  const { sendMessage: sendToDetached } = useWhiteboardBroadcastChannel({
    sessionId,
    role: 'main',
    onMessage: handleDetachedMessage,
    enabled: isTeacherUser, // Only teachers use the BroadcastChannel on main tab
  });

  // Keep sendToDetached ref updated
  useEffect(() => {
    sendToDetachedRef.current = sendToDetached;
  }, [sendToDetached]);

  // Process pending messages when canvas becomes available
  useEffect(() => {
    const processPendingMessages = () => {
      if (!fabricCanvasRef.current || pendingMessagesRef.current.length === 0) {
        return;
      }
      
      const messages = [...pendingMessagesRef.current];
      pendingMessagesRef.current = [];
      
      messages.forEach(msg => {
        processSyncMessage(msg);
      });
    };
    
    // Check periodically for pending messages (canvas might not be ready immediately)
    const interval = setInterval(processPendingMessages, 100);
    
    return () => clearInterval(interval);
  }, [processSyncMessage]);

  // Register a locally created object (helper to prevent copy-paste drift)
  const registerLocalObject = useCallback((obj) => {
    // If the object already has an objectId, it was loaded/synced remotely — skip
    if (obj.objectId) {
      return;
    }
    
    obj.objectId = generateObjectId();
    obj.authorIdentity = localParticipantIdentity;
    objectsByIdRef.current.set(obj.objectId, obj);
    
    // Prepare and broadcast (simplify path if possible, chunking handles large messages)
    const fabricJSON = prepareForBroadcast(obj.toJSON(['objectId', 'authorIdentity']));
    broadcastMessage({
      type: 'wb_object_added',
      boardId: 'main',
      objectId: obj.objectId,
      authorIdentity: obj.authorIdentity,
      fabricJSON,
    });
  }, [generateObjectId, localParticipantIdentity, broadcastMessage]);

  // Handle path created (stroke completed)
  const handlePathCreated = useCallback((path) => {
    registerLocalObject(path);
  }, [registerLocalObject]);

  // ===== LIVE STROKE HANDLERS =====
  
  // Handle stroke start - broadcast to other participants
  const handleStrokeStart = useCallback((data) => {
    if (isApplyingRemoteRef.current) return;
    
    broadcastMessage({
      type: 'wb_stroke_start',
      boardId: 'main',
      strokeId: data.strokeId,
      authorIdentity: localParticipantIdentity,
      point: data.point,
      color: data.color,
      width: data.width,
      opacity: data.opacity,
    });
  }, [broadcastMessage, localParticipantIdentity]);
  
  // Handle stroke point - broadcast intermediate points
  const handleStrokePoint = useCallback((data) => {
    if (isApplyingRemoteRef.current) return;
    
    broadcastMessage({
      type: 'wb_stroke_point',
      boardId: 'main',
      strokeId: data.strokeId,
      authorIdentity: localParticipantIdentity,
      point: data.point,
    });
  }, [broadcastMessage, localParticipantIdentity]);
  
  // Handle stroke end - notify completion (final path comes via wb_object_added)
  const handleStrokeEnd = useCallback((data) => {
    if (isApplyingRemoteRef.current) return;
    
    broadcastMessage({
      type: 'wb_stroke_end',
      boardId: 'main',
      strokeId: data.strokeId,
      authorIdentity: localParticipantIdentity,
    });
  }, [broadcastMessage, localParticipantIdentity]);

  // Handle shape created (rectangle, circle, line, arrow)
  const handleShapeCreated = useCallback((shape) => {
    registerLocalObject(shape);
  }, [registerLocalObject]);

  // Handle text created (when user clicks on canvas with text tool)
  const handleTextCreated = useCallback((textbox) => {
    registerLocalObject(textbox);
  }, [registerLocalObject]);

  // Handle image added (with optional position)
  const handleAddImage = useCallback((base64Data, width, height, posX, posY) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    
    // Create image from base64
    const imgElement = new Image();
    imgElement.onload = () => {
      const fabric = canvas._fabricModule;
      if (!fabric) return;
      
      const fabricImage = new fabric.Image(imgElement, {
        left: posX ?? 100,
        top: posY ?? 100,
        scaleX: Math.min(1, 400 / width), // Scale to max 400px display
        scaleY: Math.min(1, 400 / height),
        selectable: true,
        evented: true,
      });
      
      canvas.add(fabricImage);
      canvas.setActiveObject(fabricImage);
      canvas.renderAll();
      
      registerLocalObject(fabricImage);
    };
    imgElement.src = base64Data;
  }, [registerLocalObject]);

  // Handle object erased — debounce renderAll for rapid erasing
  const eraserRenderPendingRef = useRef(false);
  const handleObjectErased = useCallback((obj) => {
    if (!obj) return;
    
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    
    // Remove from canvas (no immediate renderAll — batched via rAF)
    canvas.remove(obj);
    
    // If the object has no objectId, it's an orphan/phantom — just remove locally
    if (!obj.objectId) {
      if (!eraserRenderPendingRef.current) {
        eraserRenderPendingRef.current = true;
        requestAnimationFrame(() => {
          eraserRenderPendingRef.current = false;
          if (fabricCanvasRef.current) fabricCanvasRef.current.renderAll();
        });
      }
      return;
    }
    
    // Remove from tracking + add tombstone
    objectsByIdRef.current.delete(obj.objectId);
    removedIdsRef.current.add(obj.objectId);
    
    // Broadcast removal
    broadcastMessage({
      type: 'wb_object_removed',
      boardId: 'main',
      objectId: obj.objectId,
      authorIdentity: localParticipantIdentity,
    });
    
    // Debounced render — only one renderAll per animation frame
    if (!eraserRenderPendingRef.current) {
      eraserRenderPendingRef.current = true;
      requestAnimationFrame(() => {
        eraserRenderPendingRef.current = false;
        if (fabricCanvasRef.current) {
          fabricCanvasRef.current.renderAll();
        }
      });
    }
  }, [broadcastMessage, localParticipantIdentity]);

  // Handle object modified
  const handleObjectModified = useCallback((obj, newTransform, prevTransform) => {
    if (!obj.objectId) return; // Orphan — can't broadcast without objectId
    
    // Broadcast
    broadcastMessage({
      type: 'wb_object_modified',
      boardId: 'main',
      objectId: obj.objectId,
      authorIdentity: localParticipantIdentity,
      transform: newTransform,
    });
  }, [localParticipantIdentity, broadcastMessage]);

  // Handle selection change
  const handleSelectionChange = useCallback((selected) => {
    setHasSelection(selected && selected.length > 0);
  }, []);

  // Delete selected objects
  const deleteSelected = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    
    const active = canvas.getActiveObject();
    if (!active) return;
    
    const objects = active.type === 'activeSelection' 
      ? active.getObjects() 
      : [active];
    
    objects.forEach(obj => {
      // If the object has no objectId, it's an orphan/phantom — remove locally only
      if (!obj.objectId) {
        canvas.remove(obj);
        return;
      }
      
      // Permission check: students can only delete own objects
      if (!isTeacherUser && obj.authorIdentity !== localParticipantIdentity) {
        return;
      }
      
      canvas.remove(obj);
      objectsByIdRef.current.delete(obj.objectId);
      removedIdsRef.current.add(obj.objectId);
      
      // Broadcast
      broadcastMessage({
        type: 'wb_object_removed',
        boardId: 'main',
        objectId: obj.objectId,
        authorIdentity: localParticipantIdentity,
      });
    });
    
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setHasSelection(false);
  }, [isTeacherUser, localParticipantIdentity, broadcastMessage]);

  // Clear canvas (teacher only)
  const clearCanvas = useCallback(() => {
    if (!isTeacherUser) return;
    
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    
    canvas.clear();
    canvas.backgroundColor = '#ffffff';
    objectsByIdRef.current.clear();
    removedIdsRef.current.clear();
    canvas.requestRenderAll();
    
    broadcastMessage({
      type: 'wb_clear',
      boardId: 'main',
      authorIdentity: localParticipantIdentity,
    });
    
    toast.success('Whiteboard cleared');
  }, [isTeacherUser, localParticipantIdentity, broadcastMessage]);

  // Publish egress track (teacher only) - for recording
  const publishEgressTrack = useCallback(async () => {
    if (!isTeacherUser || !roomRef.current || !fabricCanvasRef.current) return;
    
    try {
      const canvas = fabricCanvasRef.current;
      const canvasEl = canvas.lowerCanvasEl;
      
      if (!canvasEl || typeof canvasEl.captureStream !== 'function') {
        console.warn('[Whiteboard] captureStream not supported');
        return;
      }
      
      // Capture stream from canvas at 10 FPS
      const stream = canvasEl.captureStream(10);
      egressStreamRef.current = stream;
      
      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        console.warn('[Whiteboard] No video track from captureStream');
        return;
      }
      
      // Create LiveKit local video track
      const { LocalVideoTrack } = await import('livekit-client');
      const localTrack = new LocalVideoTrack(videoTrack);
      localTrack.source = Track.Source.ScreenShare;
      
      // Publish with name 'whiteboard' - this is how we identify it for filtering
      await roomRef.current.localParticipant.publishTrack(localTrack, {
        name: 'whiteboard',
        simulcast: false,
        source: Track.Source.ScreenShare,
      });
      
      egressTrackRef.current = localTrack;
    } catch (err) {
      console.error('[Whiteboard] Failed to publish egress track:', err);
    }
  }, [isTeacherUser, roomRef]);

  // Unpublish egress track
  const unpublishEgressTrack = useCallback(async () => {
    if (!roomRef.current || !egressTrackRef.current) return;
    
    try {
      await roomRef.current.localParticipant.unpublishTrack(egressTrackRef.current);
      egressTrackRef.current.stop();
      egressTrackRef.current = null;
      
      if (egressStreamRef.current) {
        egressStreamRef.current.getTracks().forEach(t => t.stop());
        egressStreamRef.current = null;
      }
      
    } catch (err) {
      console.error('[Whiteboard] Failed to unpublish egress track:', err);
    }
  }, [roomRef]);

  // Keep unpublishEgressTrack ref updated for BroadcastChannel handler
  useEffect(() => {
    unpublishEgressTrackRef.current = unpublishEgressTrack;
  }, [unpublishEgressTrack]);

  // Open whiteboard (teacher only)
  const openWhiteboard = useCallback(() => {
    if (!isTeacherUser) return;
    
    setIsWhiteboardActive(true);
    
    // Broadcast state
    broadcastMessage({
      type: 'whiteboard_state',
      boardId: 'main',
      active: true,
      authorIdentity: localParticipantIdentity,
    });
    
    // Publish egress track after canvas is ready
    setTimeout(() => {
      publishEgressTrack();
    }, 500);
    
    toast.success('Whiteboard opened');
  }, [isTeacherUser, localParticipantIdentity, broadcastMessage, publishEgressTrack]);

  // Close whiteboard (teacher only) — internal, without notifying detached tab
  const closeWhiteboardInternal = useCallback(() => {
    if (!isTeacherUser) return;
    
    setIsWhiteboardActive(false);
    
    // Broadcast state via LiveKit
    broadcastMessage({
      type: 'whiteboard_state',
      boardId: 'main',
      active: false,
      authorIdentity: localParticipantIdentity,
    });
    
    // Unpublish egress track
    unpublishEgressTrack();
    
    toast.info('Whiteboard closed');
  }, [isTeacherUser, localParticipantIdentity, broadcastMessage, unpublishEgressTrack]);

  // Interval ref for monitoring detached window close
  const checkClosedIntervalRef = useRef(null);

  // Close whiteboard (teacher only) — public, also closes detached tab
  const closeWhiteboard = useCallback(() => {
    // If detached, notify the detached tab to close
    if (isDetachedRef.current && sendToDetachedRef.current) {
      sendToDetachedRef.current('whiteboard_closed', {});
      setIsDetached(false);
      if (detachedWindowRef.current && !detachedWindowRef.current.closed) {
        detachedWindowRef.current.close();
      }
      detachedWindowRef.current = null;
      // Clean up the close-monitor interval
      if (checkClosedIntervalRef.current) {
        clearInterval(checkClosedIntervalRef.current);
        checkClosedIntervalRef.current = null;
      }
    }
    
    closeWhiteboardInternal();
  }, [closeWhiteboardInternal]);
  
  // Detach whiteboard — open in new tab (teacher only)
  const detachWhiteboard = useCallback(() => {
    if (!isTeacherUser || !isWhiteboardActiveRef.current) return;
    
    // Snapshot current canvas state BEFORE the component unmounts
    const canvas = fabricCanvasRef.current;
    if (canvas) {
      detachedCanvasSnapshotRef.current = canvas.toJSON(['objectId', 'authorIdentity']);
    }
    
    // Open the detached whiteboard page
    const url = `/sessions/${sessionId}/whiteboard`;
    const win = window.open(url, 'simpleclass-whiteboard', 'width=1280,height=800');
    
    // Guard: if popup was blocked, abort detach
    if (!win) {
      toast.error('Popup blocked — please allow popups for this site and try again.');
      return;
    }
    
    // Unpublish egress track — canvas will unmount so the capture stream is useless
    unpublishEgressTrack();
    
    detachedWindowRef.current = win;
    setIsDetached(true);
    
    // Clear any previous close-monitor interval
    if (checkClosedIntervalRef.current) {
      clearInterval(checkClosedIntervalRef.current);
    }
    
    // Monitor if the detached window gets closed externally (e.g. user closes the tab)
    checkClosedIntervalRef.current = setInterval(() => {
      if (win.closed) {
        clearInterval(checkClosedIntervalRef.current);
        checkClosedIntervalRef.current = null;
        setIsDetached(false);
        detachedWindowRef.current = null;
      }
    }, 1000);
    
    toast.success('Whiteboard opened in new window');
  }, [isTeacherUser, sessionId, unpublishEgressTrack]);

  // Reattach whiteboard — bring it back into the session tab
  const attachWhiteboard = useCallback(() => {
    // Request the latest canvas snapshot from the detached tab
    if (sendToDetachedRef.current) {
      sendToDetachedRef.current('request_snapshot_and_close', {});
    }
    
    // Clear the close-monitor interval (we're handling the close ourselves)
    if (checkClosedIntervalRef.current) {
      clearInterval(checkClosedIntervalRef.current);
      checkClosedIntervalRef.current = null;
    }
    
    // Fallback: if we don't get a response, close anyway after a brief delay
    setTimeout(() => {
      if (detachedWindowRef.current && !detachedWindowRef.current.closed) {
        detachedWindowRef.current.close();
      }
      setIsDetached(false);
      detachedWindowRef.current = null;
    }, 500);
  }, []);

  // Keep attachWhiteboard ref updated for BroadcastChannel handler
  useEffect(() => {
    attachWhiteboardRef.current = attachWhiteboard;
  }, [attachWhiteboard]);

  // When reattaching, load the snapshot into the freshly mounted canvas
  const prevIsDetachedRef = useRef(false);
  useEffect(() => {
    if (prevIsDetachedRef.current && !isDetached) {
      // Just reattached — load snapshot once canvas is ready
      if (detachedCanvasSnapshotRef.current) {
        const snapshot = detachedCanvasSnapshotRef.current;
        detachedCanvasSnapshotRef.current = null;
        
        let attempts = 0;
        let loaded = false;
        let renderTimer = null;
        
        const poll = setInterval(() => {
          attempts++;
          
          if (!loaded) {
            const canvas = fabricCanvasRef.current;
            if (!canvas) {
              if (attempts > 50) { // 5s max
                clearInterval(poll);
                publishEgressTrack();
              }
              return;
            }
            
            // Load the snapshot
            isApplyingRemoteRef.current++;
            loaded = true;
            clearInterval(poll);
            
            let finalized = false;
            const finalize = () => {
              if (finalized) return;
              finalized = true;
              
              // fabricReviver in loadFromJSON call restores objectId/authorIdentity
              // Safety net: force-restore from source JSON by index
              restoreCustomProps(canvas, snapshot);
              
              objectsByIdRef.current.clear();
              canvas.getObjects().forEach(obj => {
                if (obj.objectId) objectsByIdRef.current.set(obj.objectId, obj);
              });
              isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
              publishEgressTrack();
              
              // Aggressive render loop: force repaint every 50ms for 2 seconds.
              // Objects load fine but CSS layout transitions and ResizeObserver
              // can clear the canvas bitmap after our initial renderAll.
              let renderCount = 0;
              renderTimer = setInterval(() => {
                const c = fabricCanvasRef.current;
                if (c) c.requestRenderAll();
                renderCount++;
                if (renderCount >= 40) { // 40 * 50ms = 2s
                  clearInterval(renderTimer);
                  renderTimer = null;
                }
              }, 50);
            };
            
            // Fabric.js v6: loadFromJSON(json, reviver?) → Promise
            try {
              canvas.loadFromJSON(snapshot, fabricReviver)
                .then(finalize)
                .catch(() => {
                  isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
                });
            } catch (e) {
              isApplyingRemoteRef.current = Math.max(0, isApplyingRemoteRef.current - 1);
            }
          }
        }, 100);
        
        return () => {
          clearInterval(poll);
          if (renderTimer) clearInterval(renderTimer);
        };
      } else {
        // No snapshot available
        setTimeout(() => publishEgressTrack(), 500);
      }
    }
    prevIsDetachedRef.current = isDetached;
  }, [isDetached, publishEgressTrack]);

  // Toggle student drawing permission
  const toggleStudentDrawing = useCallback((enabled) => {
    if (!isTeacherUser) return;
    
    setStudentDrawingEnabled(enabled);
    if (!enabled) {
      setAllowedStudents(null);
    }
    
    broadcastMessage({
      type: 'whiteboard_permission',
      boardId: 'main',
      canDraw: enabled,
      allowedIdentities: enabled ? allowedStudents : null,
    });
  }, [isTeacherUser, allowedStudents, broadcastMessage]);

  // Toggle individual student permission
  const toggleStudentPermission = useCallback((identity) => {
    if (!isTeacherUser || !studentDrawingEnabled) return;
    
    setAllowedStudents(prev => {
      let newAllowed;
      
      if (prev === null) {
        // Currently all allowed, clicking one means only that one
        newAllowed = [identity];
      } else if (prev.includes(identity)) {
        // Remove from list
        newAllowed = prev.filter(id => id !== identity);
        if (newAllowed.length === 0) {
          newAllowed = null; // Back to all allowed
        }
      } else {
        // Add to list
        newAllowed = [...prev, identity];
      }
      
      broadcastMessage({
        type: 'whiteboard_permission',
        boardId: 'main',
        canDraw: true,
        allowedIdentities: newAllowed,
      });
      
      return newAllowed;
    });
  }, [isTeacherUser, studentDrawingEnabled, broadcastMessage]);

  // Teacher: Listen for new participants joining and send them the whiteboard state
  useEffect(() => {
    if (!isTeacherUser || !isWhiteboardActive || !roomRef.current) return;
    
    const room = roomRef.current;
    
    const handleParticipantConnected = (participant) => {
      
      // Delay to ensure participant is ready to receive messages
      setTimeout(() => {
        // Send current state
        if (broadcastMessageRef.current) {
          broadcastMessageRef.current({
            type: 'whiteboard_state',
            boardId: 'main',
            active: true,
            authorIdentity: localParticipantIdentityRef.current,
          });
          
          // Also send permissions
          broadcastMessageRef.current({
            type: 'whiteboard_permission',
            boardId: 'main',
            canDraw: studentDrawingEnabledRef.current,
            allowedIdentities: allowedStudentsRef.current,
          });
        }
      }, 1000);
    };
    
    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
    
    return () => {
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
    };
  }, [isTeacherUser, isWhiteboardActive, roomRef]);

  // Close whiteboard for students when the owner (teacher) disconnects
  useEffect(() => {
    // Only students need this
    if (isTeacherUser) return;
    if (!roomConnected || !roomRef.current) return;
    
    const room = roomRef.current;
    
    const handleParticipantDisconnected = (participant) => {
      // If the whiteboard is active and the disconnected participant is the owner
      if (remoteWhiteboardActive && whiteboardOwnerRef.current) {
        if (participant.identity === whiteboardOwnerRef.current) {
          // Teacher who opened the whiteboard has left - close it for students
          setRemoteWhiteboardActive(false);
          whiteboardOwnerRef.current = null;
          hasSyncedRef.current = false;
          
          // Clear the canvas
          const canvas = fabricCanvasRef.current;
          if (canvas) {
            canvas.clear();
            canvas.backgroundColor = '#ffffff';
            objectsByIdRef.current.clear();
            liveStrokesRef.current.clear();
            canvas.renderAll();
          }
        }
      }
    };
    
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    
    return () => {
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    };
  }, [isTeacherUser, roomConnected, roomRef, remoteWhiteboardActive]);

  // When the teacher's room disconnects unexpectedly (e.g. DUPLICATE_IDENTITY from
  // logging in on another device), unpublish the egress track and notify students
  // immediately. Without this, the whiteboard screen share track stays in the room
  // permanently and students are stuck seeing an empty canvas with no way to dismiss it.
  useEffect(() => {
    if (!isTeacherUser || !roomConnected || !roomRef.current) return;

    const room = roomRef.current;

    const handleRoomDisconnected = () => {
      if (!mountedRef.current) return;
      console.warn('[WhiteboardManager] Room disconnected — cleaning up egress track');

      // Broadcast close to students via data channel before connection fully dies
      try {
        if (broadcastMessageRef.current && isWhiteboardActiveRef.current) {
          broadcastMessageRef.current({ type: 'whiteboard_closed' });
        }
      } catch (_) {}

      // Unpublish egress track — best effort, room may already be gone
      try {
        if (egressTrackRef.current && room.localParticipant) {
          room.localParticipant.unpublishTrack(egressTrackRef.current).catch(() => {});
          egressTrackRef.current = null;
        }
      } catch (_) {}
    };

    room.on(RoomEvent.Disconnected, handleRoomDisconnected);

    return () => {
      room.off(RoomEvent.Disconnected, handleRoomDisconnected);
    };
  }, [isTeacherUser, roomConnected, roomRef]);
  // Only request if student is in a room the teacher is broadcasting to
  useEffect(() => {
    if (isTeacherUser) return;
    if (!remoteWhiteboardActive) return;
    if (hasSyncedRef.current) return;
    
    // Check if student should see the whiteboard (respects broadcast rooms)
    // Use remoteTeacherStates (WebSocket) — teacherBroadcastRooms is local user's state
    if (currentVirtualRoom) {
      const roomId = currentVirtualRoom.id || currentVirtualRoom;
      let teacherBroadcastingToMyRoom = false;
      for (const [, teacherState] of Object.entries(remoteTeacherStates)) {
        const broadcasting = teacherState?.broadcastingTo || [];
        if (broadcasting.some(r => Number(r) === Number(roomId))) {
          teacherBroadcastingToMyRoom = true;
          break;
        }
      }
      if (!teacherBroadcastingToMyRoom) {
        return; // Not broadcasting to this student's room
      }
    }
    
    // Small delay to ensure canvas is ready
    const timer = setTimeout(() => {
      hasSyncedRef.current = true;
      broadcastMessage({
        type: 'whiteboard_sync_request',
        boardId: 'main',
        requesterIdentity: localParticipantIdentity,
      });
    }, 500);
    
    return () => clearTimeout(timer);
  }, [isTeacherUser, remoteWhiteboardActive, currentVirtualRoom, remoteTeacherStates, localParticipantIdentity, broadcastMessage]);

  // Student: Request whiteboard state on initial room connection
  // This handles the case where student joins/rejoins while whiteboard is already active
  useEffect(() => {
    if (isTeacherUser) return;
    if (!roomConnected) return;
    
    // Delay to ensure listener is set up
    const timer = setTimeout(() => {
      broadcastMessage({
        type: 'whiteboard_state_request',
        requesterIdentity: localParticipantIdentity,
      });
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [isTeacherUser, roomConnected, localParticipantIdentity, broadcastMessage]);

  // Periodic reconciliation: teacher broadcasts object manifest every 30s
  // Students diff against their local state — no canvas clear/flash
  useEffect(() => {
    if (!isTeacherUser || !isWhiteboardActive || !roomConnected || isDetached) {
      if (reconcileTimerRef.current) {
        clearInterval(reconcileTimerRef.current);
        reconcileTimerRef.current = null;
      }
      return;
    }
    
    reconcileTimerRef.current = setInterval(() => {
      const canvas = fabricCanvasRef.current;
      if (!canvas || !broadcastMessageRef.current) return;
      
      // Only reconcile if there are remote participants
      const room = roomRef.current;
      if (!room || room.remoteParticipants.size === 0) return;
      
      // Build a manifest: objectId → serialized JSON for each object
      const objects = canvas.getObjects();
      const manifest = {};
      objects.forEach(obj => {
        if (obj.objectId) {
          manifest[obj.objectId] = obj.toJSON(['objectId', 'authorIdentity']);
        }
      });
      
      broadcastMessageRef.current({
        type: 'wb_reconcile',
        boardId: 'main',
        manifest,
        authorIdentity: localParticipantIdentityRef.current,
      });
    }, 30000); // Every 30 seconds
    
    return () => {
      if (reconcileTimerRef.current) {
        clearInterval(reconcileTimerRef.current);
        reconcileTimerRef.current = null;
      }
    };
  }, [isTeacherUser, isWhiteboardActive, roomConnected, roomRef, isDetached]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      unpublishEgressTrack();
      if (reconcileTimerRef.current) {
        clearInterval(reconcileTimerRef.current);
        reconcileTimerRef.current = null;
      }
      // Clear detached window close-monitor interval
      if (checkClosedIntervalRef.current) {
        clearInterval(checkClosedIntervalRef.current);
        checkClosedIntervalRef.current = null;
      }
      // Close detached window if still open
      if (detachedWindowRef.current && !detachedWindowRef.current.closed) {
        try {
          if (sendToDetachedRef.current) {
            sendToDetachedRef.current('whiteboard_closed', {});
          }
          detachedWindowRef.current.close();
        } catch (e) { /* best effort */ }
      }
    };
  }, [unpublishEgressTrack]);

  // Determine if whiteboard should be shown
  // For students: only show if teacher is broadcasting to their room.
  // Uses remoteTeacherStates (from WebSocket) for instant reactivity.
  const shouldShowWhiteboard = (() => {
    if (isTeacherUser) {
      return isWhiteboardActive;
    }
    
    // Student: must have remote whiteboard active
    if (!remoteWhiteboardActive) {
      return false;
    }
    
    // Determine student's room. currentVirtualRoom can be briefly null during state
    // transitions even for students assigned to breakout rooms. Fall back to local
    // participant metadata to avoid briefly showing whiteboard to all students.
    let myRoomId = currentVirtualRoom?.id || currentVirtualRoom || null;
    if (myRoomId === null && roomRef.current?.localParticipant?.metadata) {
      try {
        const meta = JSON.parse(roomRef.current.localParticipant.metadata);
        if (meta.virtualRoomId !== undefined && meta.virtualRoomId !== null) {
          myRoomId = meta.virtualRoomId;
        }
      } catch (e) {}
    }
    
    // If student is in main room (no virtual room), they can see it
    if (myRoomId === null) {
      console.log('[Whiteboard] shouldShow=TRUE (main room)', {
        currentVirtualRoom,
        metadata: roomRef.current?.localParticipant?.metadata
      });
      return true;
    }
    
    // Student in breakout room: check if any teacher is broadcasting to this room
    // using remoteTeacherStates (WebSocket, instant) as primary source.
    for (const [identity, teacherState] of Object.entries(remoteTeacherStates)) {
      const broadcasting = teacherState?.broadcastingTo || [];
      if (broadcasting.some(r => Number(r) === Number(myRoomId))) {
        console.log('[Whiteboard] shouldShow=TRUE (teacher broadcasting)', {
          myRoomId, identity, broadcasting
        });
        return true;
      }
    }
    
    console.log('[Whiteboard] shouldShow=FALSE', {
      myRoomId,
      teacherStates: JSON.stringify(remoteTeacherStates),
      remoteWhiteboardActive
    });
    return false;
  })();

  // When shouldShowWhiteboard transitions false→true for students, the canvas just
  // mounted and is empty. Any previous sync data was received when the canvas didn't
  // exist (teacher opened whiteboard before broadcasting to this room). Request a
  // fresh full sync so the student gets all existing objects.
  const prevShouldShowRef = useRef(false);
  useEffect(() => {
    if (!isTeacherUser && shouldShowWhiteboard && !prevShouldShowRef.current) {
      console.log('[Whiteboard] shouldShowWhiteboard transition false→true, requesting sync');
      // Canvas just became visible — reset sync flag so sync request fires
      hasSyncedRef.current = false;
      objectsByIdRef.current.clear();
      liveStrokesRef.current.clear();
      pendingMessagesRef.current = []; // Clear stale queued messages
      
      const sendSyncRequest = () => {
        if (broadcastMessageRef.current) {
          console.log('[Whiteboard] Sending whiteboard_sync_request');
          hasSyncedRef.current = true;
          broadcastMessageRef.current({
            type: 'whiteboard_sync_request',
            boardId: 'main',
            requesterIdentity: localParticipantIdentityRef.current,
          });
        } else {
          console.warn('[Whiteboard] broadcastMessageRef not ready');
        }
      };
      
      // Send sync request after short delay to let canvas mount
      const timer = setTimeout(sendSyncRequest, 600);
      
      // Retry once more if canvas is still empty after 2s
      const retryTimer = setTimeout(() => {
        const canvas = fabricCanvasRef.current;
        if (canvas && canvas.getObjects().length === 0) {
          console.log('[Whiteboard] Canvas still empty after 2s, retrying sync request');
          hasSyncedRef.current = false;
          sendSyncRequest();
        }
      }, 2000);
      
      prevShouldShowRef.current = shouldShowWhiteboard;
      return () => {
        clearTimeout(timer);
        clearTimeout(retryTimer);
      };
    }
    prevShouldShowRef.current = shouldShowWhiteboard;
  }, [shouldShowWhiteboard, isTeacherUser]);

  // Determine if local user can draw
  const canDraw = canLocalUserDraw();

  // Get current settings
  const currentSettings = getCurrentToolSettings();

  // Cleanup: close detached window (called when leaving session)
  const cleanup = useCallback(() => {
    if (detachedWindowRef.current && !detachedWindowRef.current.closed) {
      detachedWindowRef.current.close();
    }
    detachedWindowRef.current = null;
  }, []);

  return {
    // State
    isWhiteboardActive: shouldShowWhiteboard,
    activeTool,
    toolSettings,
    activeColor: currentSettings.color,
    brushSize: currentSettings.size,
    canDraw,
    hasSelection,
    studentDrawingEnabled,
    allowedStudents,
    isDetached,
    
    // Refs
    fabricCanvasRef,
    
    // Actions
    openWhiteboard,
    closeWhiteboard,
    detachWhiteboard,
    attachWhiteboard,
    cleanup,
    setActiveTool,
    updateToolSettings,
    handlePathCreated,
    handleObjectModified,
    handleSelectionChange,
    handleStrokeStart,
    handleStrokePoint,
    handleStrokeEnd,
    handleShapeCreated,
    handleTextCreated,
    handleAddImage,
    handleObjectErased,
    deleteSelected,
    clearCanvas,
    toggleStudentDrawing,
    toggleStudentPermission,
  };
}
